import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "650px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: {
    padding: "2px",
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
    marginTop: "3px",
    marginLeft: "2rem",
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
  resetButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
}));

const DocumentCategoriesTab = (props) => {
  const { handleNext, handleBack, setDocumentCategory, featuresAccess, sponsorId } = props;
  const classes = useStyles();
  const [documentCategories, setDocumentCategories] = useState([]); // State to store document categories
  const [error, setError] = useState(""); // State to handle errors
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);
  const [savingStates, setSavingStates] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  
  
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const fetchDocumentCategories = async () => {
    setLoading(true);
    setError("");
    //console.log("Fetching document categories...");
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDocumentCategories/false`;
      
      const response = await axios.get(url);
      
      if (response.data?.status === true || response.data?.status === "true") {
        
        // Check if documentCategoryList exists and is an array
        const documentCategoryList = response.data?.documentCategoryList || [];
        
        // Map the API response to match the expected structure
        const mappedDocumentCategories = documentCategoryList.map((doc) => ({
          id: doc.documentCategoryID, // Map documentCategoryID to id
          name: doc.documentCategoryName, // Map documentCategoryName to name
          selected: !!doc.activated, // Map activated to selected, convert to boolean
          isNew: false,
          type: doc.documentCategoryType, // Map documentCategoryType to type
          lastUpdatedBy: doc.lastUpdatedBy, // Keep lastUpdatedBy info
        }));

        setDocumentCategories(mappedDocumentCategories); // Update state with mapped document categories
      } else {
        console.error("Failed to fetch document categories:", response.data?.message);
        setError(response.data?.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching document categories:", error);
      console.error("Error details:", error.response?.data);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocumentCategories(); // Fetch document categories on component mount
  }, []);

  const saveDocumentCategory = async (index) => {
    if (savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      const categoryToSave = documentCategories[index]; // Get the document category to save
      if (!categoryToSave.name.trim()) {
        setError("Please provide a name for the document category.");
        setSavingStates((prev) => ({ ...prev, [index]: false }));
        return;
      }

      // Prepare the payload for the API
      const payload = {
        sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"),
        documentCategoryList: [
          {
            documentCategoryName: categoryToSave.name.trim(), // Trim leading and trailing spaces
            documentCategoryType: "New", // Type of the document category
            activated: true, // Set to true for new categories
          },
        ],
      };

      //console.log("Saving document category with payload:", payload);

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/addDocumentCategories`;
      const response = await axios.post(url, payload);
      
      if (response.data?.status === true || response.data?.status === "true") {
        fetchDocumentCategories(); // Refresh the categories list
        if (setDocumentCategory) {
          setDocumentCategory((prev) => !prev);
        }
        // Update the state to mark the document category as saved
        setDocumentCategories((prev) =>
          prev.map((doc, i) =>
            i === index
              ? { ...doc, id: response.data?.data?.id, isNew: false, type: "New" }
              : doc
          )
        );
      } else {
        console.error("Failed to save document category:", response.data?.message);
        setError(response.data?.message || "Failed to save document category.");
      }
    } catch (error) {
      console.error("Error saving document category:", error);
      setError("An error occurred while saving the document category.");
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleCategoryToggle = (index) => {
    const newList = [...documentCategories];
    newList[index].selected = !newList[index].selected;
    newList[index].isUpdated = true;
    setDocumentCategories(newList);
    setIsUpdated(true);
  };

  const handleCategoryNameChange = (index, value) => {
    const newList = [...documentCategories];
    newList[index].name = value.trim(); // Trim leading and trailing spaces
    setDocumentCategories(newList);
    setIsUpdated(true);
  };

  const handleAddNewCategory = () => {
    setDocumentCategories([
      ...documentCategories,
      {
        name: "",
        selected: true,
        isNew: true,
        type: "New",
      },
    ]);
    setIsUpdated(true);
  };

  const handleCategoryDelete = async (index) => {
    try {
      const categoryToDelete = documentCategories[index]; // Get the document category to delete
      
      // If the category is new (not saved yet), just remove it from state
      if (categoryToDelete.isNew) {
        setDocumentCategories((prev) => prev.filter((_, i) => i !== index));
        return;
      }
      
      // Prepare the payload for the API
      const payload = {
        sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"),
        documentCategoryList: [
          {
            documentCategoryID: categoryToDelete.id, // ID of the document category
            documentCategoryName: categoryToDelete.name?.trim() || categoryToDelete.name, // Trim name
            activated: categoryToDelete.selected, // Current activation state
            markForDelete: true, // Mark the document category for deletion
          },
        ],
      };

      //console.log("Deleting document category with payload:", payload);

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDocumentCategories`;
      const response = await axios.post(url, payload);
      
      if (response.data?.status === true || response.data?.status === "true") {
        fetchDocumentCategories(); // Refresh the categories list
        if (setDocumentCategory) {
          setDocumentCategory((prev) => !prev);
        }
        // Update the state to remove the deleted document category
        setDocumentCategories((prev) => prev.filter((_, i) => i !== index));
      } else {
        console.error(
          "Failed to delete document category:",
          response.data?.message
        );
        setError(response.data?.message || "Failed to delete document category.");
      }
    } catch (error) {
      console.error("Error deleting document category:", error);
      setError("An error occurred while deleting the document category.");
    }
  };

  const handleCategoryCancel = (index) => {
    const categoryToCancel = documentCategories[index];

    // If the document category is new, remove it from the state
    if (categoryToCancel.isNew) {
      setDocumentCategories((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    // If the document category is not new, revert its changes
    setDocumentCategories((prev) =>
      prev.map((doc, i) =>
        i === index
          ? {
              ...doc,
              name: doc.originalName || doc.name, // Revert to original name
              selected: doc.originalSelected || doc.selected, // Revert to original selected state
              isUpdated: false, // Mark as not updated
            }
          : doc
      )
    );
  };

  const handleSaveSelection = async () => {
    try {
      setSaveLoading(true);
      // Check if at least one toggle is turned on
      const isAnyToggleOn = documentCategories.filter((doc) => doc.isUpdated);

      if (!isAnyToggleOn.length) {
        setSaveLoading(false);
        return;
      }
      
      if (documentCategories.every((doc) => !doc.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        setSaveLoading(false);
        return;
      }
      setErrorMessage("");

      if (isUpdated) {
        // Prepare payload for API
        const payload = {
          sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"),
          documentCategoryList: isAnyToggleOn.map((doc) => ({
            documentCategoryID: doc.id,
            documentCategoryName: doc.name?.trim() || doc.name, // Trim leading and trailing spaces
            activated: doc.selected,
            markForDelete: false,
          })),
        };

        // Call API to update document categories
        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDocumentCategories`;
        const response = await axios.post(url, payload);

        if (
          response.data?.status === true ||
          response.data?.status === "true"
        ) {
          setIsUpdated(false);
          fetchDocumentCategories(); // Refresh the categories list
          setErrorMessage("");
        } else {
          setErrorMessage(
            response.data?.message || "Failed to update document categories"
          );
        }
      }
    } catch (error) {
      console.error("Error saving document categories:", error);
      setErrorMessage("An error occurred while saving. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleResetSelection = () => {
    if (isUpdated) {
      // Reset all changes and fetch fresh data
      fetchDocumentCategories();
      setIsUpdated(false);
      setErrorMessage(""); // Clear any error messages
    }
  };

  return (
    <div>
      <div
        className={classes.container}
        style={{ display: "flex", flexDirection: "column", gap: "24px" }}
      >
        {/* Table and Buttons Section */}
        <div style={{ display: "flex", gap: "40px", alignItems: "flex-start" }}>
          {/* Document Categories Table */}
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Selection</TableCell>
                <TableCell className={classes.headerCell}>Document Category</TableCell>
                <TableCell className={classes.headerCell}>
                  Document Category ID
                </TableCell>
                <TableCell className={classes.headerCell}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                // Show loading spinner when loading is true
                <TableRow>
                  <TableCell colSpan={4}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px",
                      }}
                    >
                      <CircularProgress />
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                // Show error message when error exists
                <TableRow>
                  <TableCell colSpan={4}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60px",
                        backgroundColor: "#ffecec",
                        color: "#b00020",
                        border: "1px solid #ff9999",
                      }}
                    >
                      <Typography>{error}</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : documentCategories.length === 0 ? (
                // Show "No Data" message when documentCategories is empty
                <TableRow>
                  <TableCell colSpan={4}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60px",
                        color: "#666",
                      }}
                    >
                      <Typography>No Document Categories Found</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                // Render document categories when data is available
                documentCategories.map((doc, index) => (
                  <TableRow key={doc.id || `new-${index}`}>
                    <TableCell className={classes.bodyCell}>
                      <Switch
                        checked={doc.selected} // Bind to the selected state
                        onChange={() => handleCategoryToggle(index)} // Handle toggle changes
                        classes={{ switchBase: classes.switchBase }}
                        disabled={!canEdit}
                      />
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {doc.isNew ? (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <TextField
                            placeholder="Type Text Here"
                            value={doc.name}
                            onChange={(e) =>
                              handleCategoryNameChange(index, e.target.value)
                            } // Handle name changes
                            onBlur={(e) =>
                              handleCategoryNameChange(index, e.target.value.trim())
                            } // Trim on blur
                            className={classes.textField}
                            variant="outlined"
                            inputProps={{
                              onBeforeInput: (e) => {
                                const allowedChars = /^[a-zA-Z0-9\s\-\/()&]+$/;
                                if (!allowedChars.test(e.data)) {
                                  e.preventDefault();
                                }
                              }
                            }}
                          />
                          <div className={classes.inlineActions}>
                            <IconButton
                              onClick={() => handleCategoryCancel(index)}
                              className={classes.iconBtn}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                // Handle save for categories
                                saveDocumentCategory(index);
                              }}
                              className={classes.iconBtn}
                              disabled={
                                !doc.name?.trim() ||
                                documentCategories.some(
                                  (d, i) =>
                                    i !== index &&
                                    d.name?.trim().toLowerCase() === doc.name?.trim().toLowerCase()
                                ) || savingStates[index]
                              }
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      ) : (
                        doc.name // Display the name for existing document categories
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {doc.isNew ? "" : doc?.id}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {canEdit && (doc?.isNew === true || doc?.type === "New") && (
                        <IconButton onClick={() => handleCategoryDelete(index)}>
                          <DeleteIcon className={classes.deleteIcon} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className={classes.buttonContainer}>
            <Button
              className={classes.saveButton}
              type="button"
              onClick={handleSaveSelection}
              disabled={!isUpdated || saveLoading || !canEdit}
              style={{
                opacity: !isUpdated || !canEdit ? 0.7 : 1, // Fade out when disabled
                color: "#fff", 
              }}
            >
              {saveLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Saving...
                  <CircularProgress size={16} style={{ color: '#fff' }} />
                </div>
              ) : (
                "Save Selection"
              )}
            </Button>

            <Button
              className={classes.resetButton}
              type="button"
              onClick={handleResetSelection}
              disabled={!isUpdated || !canEdit}
              style={{
                opacity: !isUpdated || !canEdit ? 0.8 : 1, // Fade out when disabled
              }}
            >
              Reset Selection
            </Button>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div style={{ color: "red", marginTop: 10 }} className="error-message">
          {errorMessage}
        </div>
      )}

      {canEdit && (
        <Button onClick={handleAddNewCategory} className={classes.addButton}>
          <span className={classes.addIconCircle}>+</span>
          Add New Document Category
        </Button>
      )}
    </div>
  );
};

export default DocumentCategoriesTab;
